import { useMemo, useRef } from 'react'
import { taskMetrics, pipelineMetrics, engagementMetrics, crmMetrics, monthlyCounts } from '../lib/metrics.js'
import { fmtMoney, fmtDate, tsToDate } from '../lib/format.js'
import { serializeRoute } from '../lib/hashRoute.js'
import { getView } from '../lib/navPersist.js'
import { BarChart, MonthlyBars } from '../components/charts/Bars.jsx'
import Avatar from '../components/Avatar.jsx'
import UpcomingCalendarCard from '../components/UpcomingCalendarCard.jsx'
import { Card, Stat, Grid, Eyebrow, useEnterStagger } from '../ds/index.js'

const headerWrap = { padding: 'var(--space-6) var(--spacing-page) var(--space-3)' }
const sectionWrap = { margin: '8px 24px 0' }

function greetingFor(user, today) {
  const hour = today.getHours()
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const raw = user?.displayName || user?.name || user?.email || ''
  const firstName = raw.includes('@') ? raw.split('@')[0] : (raw.trim().split(/\s+/)[0] || '')
  return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting
}

/**
 * Presentational Home dashboard. All display data is derived from the metrics
 * helpers (../lib/metrics.js) via useMemo — this view never recomputes
 * aggregations itself, it only shapes/labels the returned data for charts.
 */
export default function HomeView({
  user, tasks = [], statusesById = {}, engagements = [],
  engagementStages = [], contacts = [], companies = [], teamDates = [], usersById = {},
  today = new Date(),
}) {
  const uid = user?.uid
  const tm = useMemo(() => taskMetrics(tasks, statusesById, { uid, today }), [tasks, statusesById, uid, today])
  const dm = useMemo(() => pipelineMetrics(engagements, engagementStages, { today }), [engagements, engagementStages, today])
  const em = useMemo(() => engagementMetrics(engagements, engagementStages), [engagements, engagementStages])
  const cm = useMemo(() => crmMetrics(contacts, companies), [contacts, companies])
  const contactsByMonth = useMemo(() => monthlyCounts(contacts, { months: 6, today }), [contacts, today])
  const engagementsByMonth = useMemo(() => monthlyCounts(engagements, { months: 6, today }), [engagements, today])

  const upcomingTeamDates = useMemo(() => {
    const windowStart = today.getTime()
    const windowEnd = windowStart + 30 * 86400000
    // teamDates docs use `start` (see lib/data.js createTeamDate) for the date field.
    return teamDates
      .filter((d) => { const dt = tsToDate(d.start); return dt && dt.getTime() >= windowStart && dt.getTime() <= windowEnd })
      .sort((a, b) => tsToDate(a.start) - tsToDate(b.start))
  }, [teamDates, today])

  const byPhaseData = useMemo(() => ([
    { id: 'p1', name: '30', color: '#C8A84B', count: tm.workspace.byPhase.p1 || 0 },
    { id: 'p2', name: '60', color: '#4A7CC9', count: tm.workspace.byPhase.p2 || 0 },
    { id: 'p3', name: '90', color: '#3DB89A', count: tm.workspace.byPhase.p3 || 0 },
  ]), [tm])

  const advisoryBars = useMemo(() => em.byType.advisory.map((s) => ({ id: s.stageId, name: s.name, color: '#3DB89A', count: s.count })), [em])
  const speakingBars = useMemo(() => em.byType.speaking.map((s) => ({ id: s.stageId, name: s.name, color: '#C8A84B', count: s.count })), [em])
  const prBars = useMemo(() => em.byType.pr.map((s) => ({ id: s.stageId, name: s.name, color: '#4A7CC9', count: s.count })), [em])

  const greeting = greetingFor(user, today)
  const dateLabel = fmtDate(today)
  const href = (route) => '#' + serializeRoute(route)
  const linkWrap = { textDecoration: 'none', color: 'inherit', display: 'block' }
  // Seed the saved per-project view into deep links so a "Next due" click honors
  // the user's board/calendar preference (mirrors openTaskInProject). Because the
  // URL now carries the real view, landing here can't clobber that saved view with
  // the 'list' default. (list is the default, so it stays omitted from the URL.)
  const taskHref = (projectId, taskId) => {
    const saved = getView(projectId)
    return href({ type: 'project', projectId, query: { task: taskId, ...(saved && saved !== 'list' ? { view: saved } : {}) } })
  }

  // Home data is present on first render via props/useMemo, so dep: 1 plays on mount.
  const statsRef = useRef(null)
  useEnterStagger(statsRef, { dep: 1 })
  const personalCardsRef = useRef(null)
  useEnterStagger(personalCardsRef, { dep: 1 })
  const orgCardsRef = useRef(null)
  useEnterStagger(orgCardsRef, { dep: 1 })

  return (
    <div>
      <div style={headerWrap}>
        <Eyebrow>Home</Eyebrow>
        <h1 className="font-display" style={{ fontSize: 'var(--display-md)', margin: 0, textTransform: 'uppercase', color: 'var(--color-white)', lineHeight: 0.95 }}>{greeting}</h1>
        <div className="font-mono" style={{ color: 'var(--color-muted)', fontSize: 12, marginTop: 6 }}>{dateLabel}</div>
      </div>

      {/* ── Personal section ── */}
      <div style={sectionWrap}>
        <Grid ref={statsRef} min="150px" gap="16px" style={{ marginBottom: 16 }}>
          <a href={href({ type: 'mytasks' })} style={linkWrap}>
            <Card className="cn-card--link">
              <Stat countTo={tm.mine.open} label="Open" />
            </Card>
          </a>
          <a href={href({ type: 'mytasks', query: { bucket: 'duesoon' } })} style={linkWrap}>
            <Card className="cn-card--link">
              <Stat countTo={tm.mine.dueSoon} label="Due in 14 days" accent />
            </Card>
          </a>
          <a href={href({ type: 'mytasks', query: { bucket: 'overdue' } })} style={linkWrap}>
            <Card className="cn-card--link">
              <Stat countTo={tm.mine.overdue} label="Overdue" className={tm.mine.overdue > 0 ? 'cn-stat--danger' : ''} />
            </Card>
          </a>
        </Grid>
        <Grid ref={personalCardsRef} min="220px" style={{ alignItems: 'stretch' }}>
          <Card title="My tasks by status">
            <BarChart data={tm.mine.byStatus} />
          </Card>
          <Card title="Next due">
            {tm.mine.nextDue.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>Nothing due.</div>}
            {tm.mine.nextDue.map((t) => (
              <a key={t.id} href={taskHref(t.projectId, t.id)} style={linkWrap}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--surface-row)' }}>
                  <span style={{ fontSize: 13, color: 'var(--color-white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                  <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)', flexShrink: 0 }}>{fmtDate(tsToDate(t.dueDate))}</span>
                </div>
              </a>
            ))}
          </Card>
          <Card title="Upcoming team dates">
            {upcomingTeamDates.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>Nothing in the next 30 days.</div>}
            {upcomingTeamDates.map((d) => {
              const owner = usersById[d.userUid] || usersById[d.uid]
              return (
                <a key={d.id} href={href({ type: 'team' })} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--surface-row)' }}>
                    {owner && <Avatar user={owner} size={20} />}
                    <span style={{ fontSize: 13, color: 'var(--color-white)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.who || owner?.name || ''}</span>
                    <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)' }}>{fmtDate(tsToDate(d.start))}</span>
                    {d.label && <span style={{ fontSize: 11, color: 'var(--color-muted)' }}>{d.label}</span>}
                  </div>
                </a>
              )
            })}
          </Card>
          <Card>
            <UpcomingCalendarCard />
          </Card>
        </Grid>
      </div>

      {/* ── Org KPIs section ── */}
      <div style={{ ...sectionWrap, marginTop: 28, marginBottom: 24 }}>
        <Eyebrow>Org KPIs</Eyebrow>
        <Grid ref={orgCardsRef} min="220px" style={{ marginTop: 12 }}>
          {/* Pipeline panel — Wave 2: reads engagements directly. The `deals`
              mirror it used to sum is gone; the numbers, their weighting and
              every Wave 1 drill-down link are otherwise unchanged. */}
          <Card title="Pipeline">
            <Grid min="100px" gap="20px" style={{ marginBottom: 12 }}>
              <a href={href({ type: 'engagements', board: 'speaking' })} className="cn-stat-link">
                <Stat countTo={dm.rawOpen} format={fmtMoney} label="Open pipeline" />
              </a>
              <a href={href({ type: 'engagements', board: 'speaking' })} className="cn-stat-link">
                <Stat countTo={dm.weightedOpen} format={fmtMoney} label="Weighted / PWP" accent />
              </a>
              <a href={href({ type: 'engagements', board: 'speaking' })} className="cn-stat-link">
                <Stat countTo={dm.openCount} label="Open engagements" />
              </a>
              <Stat countTo={dm.wonThisMonth.count} label={`Won this month · ${fmtMoney(dm.wonThisMonth.amount)}`} />
            </Grid>
            {/* No by-stage bar chart here on purpose. Stages are now per-type, so
                charting all of them would render five repeated status names three
                times over with nothing to tell them apart — and the Engagements
                card below already breaks stages down per type. This card answers
                "how much money", that one answers "where is everything". */}
          </Card>

          {/* Engagements panel */}
          <Card title="Engagements">
            <div style={{ marginBottom: 12 }}>
              <span className="tag-label" style={{ color: 'var(--color-muted)', fontSize: 10 }}>Advisory</span>
              <div style={{ marginTop: 6 }}><BarChart data={advisoryBars} hrefFor={(item) => href({ type: 'engagements', board: 'advisory', query: { stage: item.id } })} /></div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <span className="tag-label" style={{ color: 'var(--color-muted)', fontSize: 10 }}>Speaking</span>
              <div style={{ marginTop: 6 }}><BarChart data={speakingBars} hrefFor={(item) => href({ type: 'engagements', board: 'speaking', query: { stage: item.id } })} /></div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <span className="tag-label" style={{ color: 'var(--color-muted)', fontSize: 10 }}>PR &amp; Media</span>
              <div style={{ marginTop: 6 }}><BarChart data={prBars} hrefFor={(item) => href({ type: 'engagements', board: 'pr', query: { stage: item.id } })} /></div>
            </div>
            <Stat countTo={em.confirmedFees} format={fmtMoney} label="Confirmed fees" accent />
          </Card>

          {/* CRM growth panel */}
          <Card title="CRM growth">
            <Grid min="100px" gap="20px" style={{ marginBottom: 12 }}>
              <a href={href({ type: 'crm', object: 'contacts' })} className="cn-stat-link">
                <Stat countTo={cm.contacts} label="Contacts" />
              </a>
              <a href={href({ type: 'crm', object: 'companies' })} className="cn-stat-link">
                <Stat countTo={cm.companies} label="Companies" />
              </a>
            </Grid>
            <div style={{ marginBottom: 12 }}>
              <span className="tag-label" style={{ color: 'var(--color-muted)', fontSize: 10 }}>New contacts / mo</span>
              <div style={{ marginTop: 6 }}><MonthlyBars data={contactsByMonth} color="#4A7CC9" /></div>
            </div>
            <div>
              <span className="tag-label" style={{ color: 'var(--color-muted)', fontSize: 10 }}>New engagements / mo</span>
              <div style={{ marginTop: 6 }}><MonthlyBars data={engagementsByMonth} color="var(--color-gold)" /></div>
            </div>
          </Card>

          {/* Task health is workspace-wide; My Tasks is personal-only. Linking
              these numbers there would open a list that contradicts the count.
              Same for "Confirmed fees" (spans both boards) and the by-phase
              bars (no filtered destination exists). Leave them unlinked until
              a workspace-wide task list exists. "Won this month" is unlinked
              for the same reason: it is time-scoped to the current month,
              while the board's stage filter has no time window and would
              show every engagement ever won. Open pipeline / Weighted / Open
              engagements, by contrast, are accepted as linking to the plain
              Speaking board (`#/speaking`; board is the default view, so the
              param is omitted) even though that board is both a superset in one
              direction (it also renders past and declined engagements) and a
              subset in another (it excludes Advisory and PR). No open-only,
              all-type list exists,
              and the board's stage columns make open-ness visible at a glance,
              so the asymmetry with "Won this month" is judged acceptable. */}
          {/* Task health panel */}
          <Card title="Task health">
            <Grid min="100px" gap="20px" style={{ marginBottom: 12 }}>
              <Stat countTo={tm.workspace.open} label="Open" />
              <Stat countTo={tm.workspace.done} label="Done" />
              <Stat countTo={tm.workspace.overdue} label="Overdue" className={tm.workspace.overdue > 0 ? 'cn-stat--danger' : ''} />
            </Grid>
            <span className="tag-label" style={{ color: 'var(--color-muted)', fontSize: 10 }}>By phase (days)</span>
            <div style={{ marginTop: 6 }}><BarChart data={byPhaseData} /></div>
          </Card>
        </Grid>
      </div>
    </div>
  )
}
